"use client";

import { useEffect, useState } from "react";
import { useIsWide } from "@/components/ui/design-system/use-is-wide";

/**
 * Whether a `position: sticky; bottom: N` element is pinned to the viewport
 * rather than resting in its place in the flow, and how tall it is, for
 * whatever else on the page has to stop short of it. CSS cannot say; this watches
 * a zero-height sentinel placed directly after the element. While the
 * sentinel is below the viewport's edge (less the element's own `bottom`
 * offset, so a tab bar under it does not count as "in view"), the element
 * is pinned; once the sentinel rises past that edge the element is where
 * the flow puts it.
 *
 * Callback refs, as `useRestingTop` uses: the elements mount after the
 * page's data is in, so a ref object would be read once, empty, and never
 * again.
 *
 * `stuck` starts true: an element that is pinned on first paint keeps its
 * raised look rather than flashing flat, and the observer fires at once to
 * correct the other case.
 *
 * The `bottom` offset is read once per layout, not once: it changes at the
 * rail width (a tab bar and its inset on a phone, a step above the edge at
 * a desk), so a tablet turned on its side would otherwise keep the margin
 * of the layout it left. The observer is rebuilt when that width flips.
 */
export function useStuckAtBottom(): {
  ref: (node: HTMLElement | null) => void;
  sentinelRef: (node: HTMLElement | null) => void;
  stuck: boolean;
  /** The element's outer height, kept current as its text rewraps. */
  height: number;
} {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const [sentinel, setSentinel] = useState<HTMLElement | null>(null);
  const [stuck, setStuck] = useState(true);
  const [height, setHeight] = useState(0);
  const wide = useIsWide();

  useEffect(() => {
    if (!node) return;
    const measure = () => setHeight(Math.round(node.offsetHeight));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  useEffect(() => {
    if (!node || !sentinel) return;
    // The resolved `bottom`, so `calc(var(--one-tabbar) + env(...))` comes
    // back as the pixels it worked out to. "auto" (a hidden element) is 0.
    const offset = Number.parseFloat(getComputedStyle(node).bottom) || 0;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (entry) setStuck(!entry.isIntersecting);
      },
      { rootMargin: `0px 0px -${offset}px 0px` },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
    // `wide` is not read in the effect; it is what changes `bottom`.
  }, [node, sentinel, wide]);

  return { ref: setNode, sentinelRef: setSentinel, stuck, height };
}
